/*
 * The twin's particle material, for everything that draws it: the WebGL
 * swarm inside the glass, and the hero's egress, which draws the same
 * particles leaving it (evidence-egress).
 */

// Six vivid families drawn from the editorial themes, tuned to a narrow
// perceptual-luminance band. Generating them by mixing each theme's dark and
// accent values produced a 3x brightness spread: teal disappeared into white
// while lavender read almost black. These remain recognisably claret, orange,
// warm accent, mint, seafoam and lavender, but every family now carries equal
// visual weight in both the mixed suspension and the ordered layers.
export const BRAND_PARTICLE_COLOURS = [
  "#D91B5C",
  "#E8501C",
  "#D49A00",
  "#00A56A",
  "#008FB8",
  "#7456E8",
] as const satisfies readonly [string, string, string, string, string, string];

/** The settled rings, innermost first. */
export const RING_COUNT = BRAND_PARTICLE_COLOURS.length;

/**
 * The family that settles on a ring, innermost ring 0: the swarm's shader
 * gives the first family the outermost ring (its `layerOrder`).
 */
export function ringColour(ring: number): string {
  return BRAND_PARTICLE_COLOURS[RING_COUNT - 1 - ring] ?? BRAND_PARTICLE_COLOURS[0];
}

/**
 * A settled ring's middle and half its width, as fractions of the glass's
 * radius. From the swarm's shader and camera: a ring sits at 0.012 + 0.150
 * a ring + 0.018 a sub-layer (eight of them) in object units, and the
 * camera (3.1 away, 40° field of view) shows 1.128 units across the glass's
 * radius.
 */
const UNITS_ACROSS_RADIUS = 3.1 * Math.tan((20 * Math.PI) / 180);
const RING_BAND = 7 * 0.018;

export function ringRadius(ring: number): { middle: number; halfWidth: number } {
  const inner = 0.012 + ring * 0.15;
  return { middle: (inner + RING_BAND / 2) / UNITS_ACROSS_RADIUS, halfWidth: RING_BAND / 2 / UNITS_ACROSS_RADIUS };
}
